import { PROPERTY_GRID_SUBJECT_PROPERTY, SubjectData, noop } from '@aloft/toolkit-shared';
import { Flex, IconButton, Switch, Typography, useModal } from '@aloft/toolkit-sui';

import { FieldPath, useWatch } from 'react-hook-form';

import {
  selectDefaultFeatureAreas,
  selectHasArvOption,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectAivArvActiveTab } from '../../../redux/features/selectors/uiSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { PropertyGridForm, PropertyGridSectionProps } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import {
  applyEstimatedFeatureArea,
  getGridSourceFeatureAreas,
} from '../utils/applyEstimatedFeatureArea.ts';

type TitleRowProps = Pick<
  PropertyGridSectionProps,
  'columnIndex' | 'feature' | 'fieldTypes' | 'isRemovable' | 'onRemove' | 'control' | 'getValues'
> & {
  title: string;
};

const MarkFeatureAsNoneComponent = ({
  columnIndex,
  title,
  feature,
  control,
  getValues,
}: TitleRowProps) => {
  const dispatch = useAppDispatch();
  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);
  const defaultFeatureAreas = useAppSelector(selectDefaultFeatureAreas);
  const column = useWatch({ control, name: `columns.${columnIndex}` });
  const { propertyData, propertyId, type } = column ?? {};
  const isOtherFeature = feature === 'other';
  const hasFeatureKey = feature === 'manualCost' ? 'hasManualCost' : `${feature}.hasFeature`;
  const hasFeature = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.improvements.${hasFeatureKey}` as FieldPath<PropertyGridForm>,
  }) as boolean;
  const hasArvFeature = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.arv.improvements.${hasFeatureKey}` as FieldPath<PropertyGridForm>,
  }) as boolean;
  const otherDisplayName = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.improvements.otherAdjustmentsDisplayName` as FieldPath<PropertyGridForm>,
  }) as string | undefined;
  if (
    !propertyData ||
    !propertyId ||
    !type ||
    (isOtherFeature && propertyId === PROPERTY_GRID_SUBJECT_PROPERTY)
  )
    return null;

  let hasFeatureValue = isOtherFeature ? Boolean(otherDisplayName?.trim()) : hasFeature;
  if (!isOtherFeature && hasArvOption && propertyId === PROPERTY_GRID_SUBJECT_PROPERTY) {
    if (activeTab === 'all') {
      hasFeatureValue = hasFeature || hasArvFeature;
    } else if (activeTab === 'arv') {
      hasFeatureValue = hasArvFeature;
    }
  }

  const handleSwitchChange = (value: boolean) => {
    if (!propertyData || !feature) return;

    if (isOtherFeature) {
      propertyData.editableData.improvements.otherAdjustmentsDisplayName = value ? '' : 'Other';
    } else if (feature === 'manualCost') {
      if (
        propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY ||
        !hasArvOption ||
        activeTab === 'all' ||
        activeTab === 'aiv'
      ) {
        propertyData.editableData.improvements.hasManualCost = !value;
      }
      if (
        propertyId === PROPERTY_GRID_SUBJECT_PROPERTY &&
        hasArvOption &&
        (activeTab === 'all' ||
          (activeTab === 'arv' && (propertyData.editableData as SubjectData['editableData']).arv))
      ) {
        (propertyData.editableData as SubjectData['editableData']).arv!.improvements.hasManualCost =
          !value;
      }
    } else {
      const enabling = !value;

      if (
        (propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY ||
          !hasArvOption ||
          activeTab === 'all' ||
          activeTab === 'aiv') &&
        propertyData.editableData.improvements
      ) {
        let improvements = {
          ...propertyData.editableData.improvements,
          [feature]: {
            ...propertyData.editableData.improvements[feature],
            hasFeature: enabling,
          },
        };
        let selectedSources = propertyData.selectedSources?.editableData?.improvements;

        if (enabling) {
          ({ improvements, selectedSources } = applyEstimatedFeatureArea({
            improvements,
            selectedSources,
            feature,
            defaultAreas: defaultFeatureAreas,
            sourceAreas: getGridSourceFeatureAreas(getValues('columns'), propertyId, feature),
          }));
        }

        propertyData.editableData.improvements = improvements;
        if (selectedSources) {
          propertyData.selectedSources = {
            ...propertyData.selectedSources,
            editableData: {
              ...propertyData.selectedSources?.editableData,
              improvements: selectedSources,
            },
          };
        }
      }

      if (
        propertyId === PROPERTY_GRID_SUBJECT_PROPERTY &&
        hasArvOption &&
        (activeTab === 'all' || activeTab === 'arv') &&
        (propertyData.editableData as SubjectData['editableData']).arv?.improvements
      ) {
        const arvEditable = (propertyData.editableData as SubjectData['editableData']).arv!;
        let improvements = {
          ...arvEditable.improvements,
          [feature]: {
            ...arvEditable.improvements[feature],
            hasFeature: enabling,
          },
        };
        let selectedSources =
          propertyData.selectedSources?.editableData?.arv?.improvements ??
          propertyData.selectedSources?.editableData?.improvements;

        if (enabling) {
          ({ improvements, selectedSources } = applyEstimatedFeatureArea({
            improvements,
            selectedSources,
            feature,
            defaultAreas: defaultFeatureAreas,
            sourceAreas: getGridSourceFeatureAreas(getValues('columns'), propertyId, feature),
          }));
        }

        (propertyData.editableData as SubjectData['editableData']).arv = {
          ...arvEditable,
          improvements,
        };
        if (selectedSources) {
          propertyData.selectedSources = {
            ...propertyData.selectedSources,
            editableData: {
              ...propertyData.selectedSources?.editableData,
              arv: {
                ...propertyData.selectedSources?.editableData?.arv,
                improvements: selectedSources,
              },
            },
          };
        }
      }
    }

    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  if (type === 'property' && feature) {
    return (
      <Flex>
        <Switch
          id={`no-${title}-${columnIndex}`}
          label="None"
          value={!hasFeatureValue}
          onChange={handleSwitchChange}
        />
      </Flex>
    );
  }

  return null;
};

const RemoveFeatureSectionComponent = ({ title, onRemove = noop }: TitleRowProps) => {
  const { Modal, isModalOpen, openModal, closeModal } = useModal();

  return (
    <>
      <IconButton
        ariaLabel="Remove feature"
        title="Remove feature"
        icon="remove"
        iconColor="textError"
        variant="tertiary"
        size="xs"
        iconSize="md"
        onClick={openModal}
      />
      {isModalOpen && (
        <Modal
          size="lg"
          isOpen={isModalOpen}
          onClose={closeModal}
          closeOnEsc
          variant="danger"
          title={`Remove ${title}?`}
          primaryAction={{
            text: 'Remove',
            onClick: () => {
              onRemove();
              closeModal();
            },
          }}
          secondaryAction={{
            text: 'Cancel',
            onClick: closeModal,
          }}
        >
          Any information entered for this feature will be lost if you remove it. Are you sure you
          want to proceed?
        </Modal>
      )}
    </>
  );
};

const HasFeatureSectionComponent = ({ columnIndex, control, feature }: TitleRowProps) => {
  const column = useWatch({ control, name: `columns.${columnIndex}` });
  const { propertyData, propertyId, type } = column ?? {};

  const hasFeatureKey = feature === 'manualCost' ? 'hasManualCost' : `${feature}.hasFeature`;
  const hasFeature = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.improvements.${hasFeatureKey}` as FieldPath<PropertyGridForm>,
  }) as boolean;

  if (
    !propertyData ||
    !propertyId ||
    !type ||
    type === 'property' ||
    feature === 'manualCost' ||
    hasFeature === undefined
  )
    return null;

  return (
    <Typography variant="h4" color={hasFeature ? 'textPrimary' : 'textSecondary'}>
      {hasFeature ? 'Yes' : 'None'}
    </Typography>
  );
};

export const TitleRow = (props: TitleRowProps) => {
  const { fieldTypes, title, isRemovable } = props;
  const showRemove = Boolean(isRemovable && fieldTypes.includes('label'));

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="surface4"
      height={PROPERTY_GRID_ROW_HEIGHT}
      padding="8px"
      gridColumn="1 / -1"
      width="100%"
      overflow="hidden"
    >
      <Flex alignItems="center">
        {fieldTypes.includes('label') && (
          <Typography variant="h3" truncate __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}>
            {title}
          </Typography>
        )}
      </Flex>
      {showRemove && <RemoveFeatureSectionComponent {...props} />}
      <HasFeatureSectionComponent {...props} />
      <MarkFeatureAsNoneComponent {...props} />
    </Flex>
  );
};
